import { request } from "../../base";
import type {
	Lead,
	KanbanData,
	LeadActivity,
	CreateLeadInput,
	UpdateLeadInput,
	ChangeStageInput,
	AddNoteInput,
} from "./types";

export const crmLeadsApi = {
	// Leads
	async getKanbanData(proId: string) {
		return request<KanbanData>(
			`/api/pro/${proId}/crm/leads`,
		);
	},

	async getLead(proId: string, leadId: number) {
		return request<Lead>(
			`/api/pro/${proId}/crm/leads/${leadId}`,
		);
	},

	async createLead(proId: string, data: CreateLeadInput) {
		return request<Lead>(`/api/pro/${proId}/crm/leads`, {
			method: "POST",
			body: data,
		});
	},

	async updateLead(
		proId: string,
		leadId: number,
		data: UpdateLeadInput,
	) {
		return request<Lead>(
			`/api/pro/${proId}/crm/leads/${leadId}`,
			{ method: "PATCH", body: data },
		);
	},

	async changeStage(
		proId: string,
		leadId: number,
		data: ChangeStageInput,
	) {
		return request<Lead>(
			`/api/pro/${proId}/crm/leads/${leadId}/stage`,
			{ method: "PATCH", body: data },
		);
	},

	async markContacted(
		proId: string,
		leadId: number,
		data: { method: string; noteContent?: string },
	) {
		return request<Lead>(
			`/api/pro/${proId}/crm/leads/${leadId}/contact`,
			{ method: "PATCH", body: data },
		);
	},

	async updateQuoteValue(
		proId: string,
		leadId: number,
		valuePaise: number,
	) {
		return request<Lead>(
			`/api/pro/${proId}/crm/leads/${leadId}/quote-value`,
			{ method: "PATCH", body: { valuePaise } },
		);
	},

	async updateOrderValue(
		proId: string,
		leadId: number,
		valuePaise: number,
	) {
		return request<Lead>(
			`/api/pro/${proId}/crm/leads/${leadId}/order-value`,
			{ method: "PATCH", body: { valuePaise } },
		);
	},

	async archiveLead(proId: string, leadId: number) {
		return request<Lead>(
			`/api/pro/${proId}/crm/leads/${leadId}/archive`,
			{ method: "PATCH" },
		);
	},

	async unarchiveLead(proId: string, leadId: number) {
		return request<Lead>(
			`/api/pro/${proId}/crm/leads/${leadId}/unarchive`,
			{ method: "PATCH" },
		);
	},

	async checkPhone(proId: string, phone: string) {
		return request<{ exists: boolean; lead?: Lead }>(
			`/api/pro/${proId}/crm/leads/check-phone/${encodeURIComponent(phone)}`,
		);
	},

	async listLeads(
		proId: string,
		filters: { isArchived?: boolean; page?: number; limit?: number } = {},
	) {
		const params = new URLSearchParams({ view: "list" });
		if (filters.isArchived !== undefined)
			params.set("isArchived", String(filters.isArchived));
		if (filters.page) params.set("page", String(filters.page));
		if (filters.limit) params.set("limit", String(filters.limit));
		return request<Lead[]>(`/api/pro/${proId}/crm/leads?${params.toString()}`);
	},

	// Notes & Activities
	async addNote(proId: string, leadId: number, data: AddNoteInput) {
		return request<{ activities: { activityType: string }[] }>(
			`/api/pro/${proId}/crm/leads/${leadId}/notes`,
			{ method: "POST", body: data },
		);
	},

	async getActivities(
		proId: string,
		leadId: number,
		offset = 0,
		limit = 30,
	) {
		const page = Math.floor(offset / limit) + 1;
		const response = await request<LeadActivity[]>(
			`/api/pro/${proId}/crm/leads/${leadId}/activities?page=${page}&limit=${limit}`,
		);
		// API returns activities as data array with total in meta
		return {
			...response,
			data: {
				activities: response.data || [],
				total: response.meta?.total ?? 0,
			},
		};
	},
};
